import { useEffect, useState } from 'react'
import {
  createAgentResourceAccess,
  deleteAgentResourceGrant,
  fetchAgentResourceGrants,
  fetchResourceConnectionDefaultRules,
  type ManifestRuleApproval,
  type ManifestRuleWithStatus,
} from '../../api/ws'
import type { AgentInstance, ResourceConnection } from '../../store/types'
import { InstallWizard } from './InstallWizard'
import '../agents/NewAgentWizard.css'

// AccessGrantSheet is Surface B — "who can use this connection?" (plan §3,
// §5). Given a household connection that already has an OAuthed (or
// credentialed) account, it manages which agents can use it: it opens
// pre-checked to whoever already has access, and submitting applies the diff
// — newly-checked agents get granted (one atomic createAgentResourceAccess),
// unchecked agents that previously had access get revoked. Scope choice:
//   - "everyone in this household" (scope=organization) — one org-scoped rule
//     set that also covers agents created later, and
//   - "just these agents" (scope=agent) — rules scoped to the checked agents.
// Bindings are minted per checked agent; the secret.use rule rides along
// server-side so no invoke gets re-prompted.
//
// Rendered as an overlay so it can drop into the connection detail without
// routing through the sheet stack.

type Step = 'agents' | 'rules' | 'submitting' | 'done'

export function AccessGrantSheet({
  connection,
  agents,
  presetAgentIDs,
  onClose,
  onDone,
}: {
  connection: ResourceConnection
  // Agents eligible to bind — the caller filters to the connection's host.
  agents: AgentInstance[]
  // Pre-checked agents (single-agent entry from an agent's Resources tab).
  presetAgentIDs?: string[]
  onClose: () => void
  onDone?: (summary: { added: number; removed: number }) => void
}) {
  const [step, setStep] = useState<Step>('agents')
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(presetAgentIDs ?? []),
  )
  // Agents that already had access when the sheet opened — the baseline the
  // submit diffs against. Also drives the "has access" badge and the initial
  // checked state.
  const [initialGranted, setInitialGranted] = useState<Set<string>>(new Set())
  const [grantsLoaded, setGrantsLoaded] = useState(false)
  const [everyone, setEveryone] = useState(false)

  const [manifest, setManifest] = useState<ManifestRuleWithStatus[]>([])
  const [manifestLoaded, setManifestLoaded] = useState(false)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<{ added: number; removed: number } | null>(null)
  // True when the last action was a "re-apply defaults" (vs an add/remove diff), so
  // the done step can speak to it. See reapplyDefaults.
  const [reapplied, setReapplied] = useState(false)

  // Load everything the sheet needs up front: the connection's manifest
  // defaults, and — per eligible agent — whether it already has access (so
  // we open pre-checked). Runs once per open.
  useEffect(() => {
    let cancelled = false


    fetchResourceConnectionDefaultRules(connection.id).then((resp) => {
      if (cancelled) return
      if (resp.error) setError(resp.error)
      setManifest(resp.manifest_defaults ?? [])
      setManifestLoaded(true)
    })

    // Who already has access? One grants-list call per eligible agent; the
    // set is small (non-retired agents on this host).
    Promise.all(
      agents.map((a) =>
        fetchAgentResourceGrants(a.id).then((resp) => ({
          id: a.id,
          hasIt: (resp.grants ?? []).some((g) => g.resource_connection_id === connection.id),
        })),
      ),
    ).then((results) => {
      if (cancelled) return
      const granted = new Set(results.filter((r) => r.hasIt).map((r) => r.id))
      setInitialGranted(granted)
      // Open pre-checked to the current members, plus any preset.
      setSelected(new Set([...granted, ...(presetAgentIDs ?? [])]))
      setGrantsLoaded(true)
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection.id])

  const connName = connection.display_name || connection.slug || connection.id
  const scope: 'organization' | 'agent' = everyone ? 'organization' : 'agent'
  const loading = !manifestLoaded || !grantsLoaded

  // Diff of the current selection against the baseline. Removals only apply in
  // per-agent mode — "everyone" is an org-wide grant that per-agent unchecking
  // can't carve an exception out of.
  const toAdd = [...selected].filter((id) => !initialGranted.has(id))
  const toRemove = everyone ? [] : [...initialGranted].filter((id) => !selected.has(id))

  function toggleAgent(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function agentsNext() {
    setError(null)
    if (toAdd.length === 0 && toRemove.length === 0) {
      setError('No changes to apply.')
      return
    }
    // Pure removals need no rule approval — apply immediately.
    if (toAdd.length === 0) {
      void applyChanges([])
      return
    }
    // Additions present → approve rules. There is no account to choose:
    // a connection authenticates as exactly one account.
    setStep('rules')
  }

  async function applyChanges(rules: ManifestRuleApproval[]) {
    setBusy(true)
    setError(null)
    setStep('submitting')
    try {
      let added = 0
      if (toAdd.length > 0) {
        const resp = await createAgentResourceAccess({
          resourceConnectionID: connection.id,
          aiAgentInstanceIDs: toAdd,
          rules,
          scope,
        })
        if (resp.error) {
          setError(resp.error)
          setStep('rules')
          return
        }
        added = resp.agents?.length ?? toAdd.length
      }
      let removed = 0
      for (const id of toRemove) {
        const resp = await deleteAgentResourceGrant({
          agentInstanceID: id,
          resourceConnectionID: connection.id,
        })
        if (resp.error) {
          setError(`Removed ${removed} of ${toRemove.length}: ${resp.error}`)
          setStep('agents')
          return
        }
        removed++
      }
      const s = { added, removed }
      setSummary(s)
      setStep('done')
      onDone?.(s)
    } catch (e) {
      setError(String(e))
      setStep(toAdd.length > 0 ? 'rules' : 'agents')
    } finally {
      setBusy(false)
    }
  }

  // Re-apply the connection's CURRENT plugin default rules to the agents that
  // already have access. Needed because a grant seeds defaults once, at grant time
  // — so when the plugin later adds a default (e.g. HA's `announce: allow` in 0.4.0),
  // existing grants don't get it and the agent keeps getting prompted. Seeding is
  // additive server-side (INSERT OR IGNORE), so this only ADDS the missing defaults;
  // it never overrides a decision the household customized. Deliberately user-initiated
  // (not automatic on upgrade) — auto-reapply would resurrect a rule someone
  // intentionally deleted.
  async function reapplyDefaults() {
    const ids = [...initialGranted]
    if (ids.length === 0 || manifest.length === 0) return
    const rules: ManifestRuleApproval[] = manifest.map((r) => ({
      action: r.action,
      decision: r.minted_decision ?? r.decision,
    }))
    setBusy(true)
    setError(null)
    setStep('submitting')
    try {
      const resp = await createAgentResourceAccess({
        resourceConnectionID: connection.id,
        aiAgentInstanceIDs: ids,
        rules,
        scope: 'agent',
      })
      if (resp.error) {
        setError(resp.error)
        setStep('agents')
        return
      }
      setReapplied(true)
      setSummary({ added: ids.length, removed: 0 })
      setStep('done')
    } catch (e) {
      setError(String(e))
      setStep('agents')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="wizard-overlay-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 200,
      }}
    >
      <div
        className="wizard wizard--sheet"
        style={{ maxHeight: 'calc(var(--vvh, 100dvh) - 40px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wizard__nav">
          <button
            className="wizard__nav-btn"
            onClick={() => {
              if (step === 'rules') setStep('agents')
              else onClose()
            }}
            disabled={busy}
          >
            {step === 'agents' || step === 'done' ? 'Close' : '‹ Back'}
          </button>
          <div className="wizard__nav-title">{titleFor(step, connName)}</div>
          <div className="wizard__nav-spacer" />
        </header>

        <div className="wizard__body">
          <div className="wizard__step">
            {error && (
              <div className="wizard__error" style={{ marginBottom: 12 }}>
                {error}
              </div>
            )}

            {step === 'agents' && (
              <AgentsStep
                agents={agents}
                selected={selected}
                granted={initialGranted}
                onToggle={toggleAgent}
                everyone={everyone}
                onEveryone={setEveryone}
                onNext={agentsNext}
                onReapply={reapplyDefaults}
                loading={loading}
                nextLabel={toAdd.length === 0 && toRemove.length > 0 ? 'Apply changes' : 'Continue'}
              />
            )}

            {step === 'rules' && (
              <>
                <p style={{ fontSize: 13, opacity: 0.7, marginTop: 0 }}>
                  {everyone
                    ? 'These rules will apply to every agent in this household, including ones you add later.'
                    : `These rules will apply to the ${toAdd.length} newly added agent${toAdd.length === 1 ? '' : 's'}.`}
                  {toRemove.length > 0 &&
                    ` ${toRemove.length} agent${toRemove.length === 1 ? '' : 's'} will also lose access.`}
                </p>
                <InstallWizard
                  manifestDefaults={manifest}
                  scopeLabel={everyone ? 'for this household' : 'for the selected agents'}
                  submitLabel="Apply"
                  busy={busy}
                  onApprove={applyChanges}
                  onCancel={onClose}
                />
              </>
            )}

            {step === 'submitting' && (
              <div className="wizard__hint" style={{ textAlign: 'center', paddingTop: 48 }}>
                ⏳ Applying…
              </div>
            )}

            {step === 'done' && summary && (
              <DoneStep
                connName={connName}
                added={summary.added}
                removed={summary.removed}
                everyone={everyone}
                reapplied={reapplied}
                onClose={onClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function AgentsStep({
  agents,
  selected,
  granted,
  onToggle,
  everyone,
  onEveryone,
  onNext,
  onReapply,
  loading,
  nextLabel,
}: {
  agents: AgentInstance[]
  selected: Set<string>
  granted: Set<string>
  onToggle: (id: string) => void
  everyone: boolean
  onEveryone: (v: boolean) => void
  onNext: () => void
  onReapply: () => void
  loading: boolean
  nextLabel: string
}) {
  if (agents.length === 0) {
    return (
      <p style={{ fontSize: 14, opacity: 0.7, lineHeight: 1.5 }}>
        No agents on this connection's host yet. Create an agent on that host,
        then come back to give it access.
      </p>
    )
  }
  return (
    <div>
      <p style={{ fontSize: 13, opacity: 0.7, marginTop: 0 }}>
        Choose which agents can use this account. Agents that already have
        access are checked — uncheck one to remove it.
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px' }}>
        {agents.map((a) => {
          const on = selected.has(a.id)
          const hasAccess = granted.has(a.id)
          return (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => onToggle(a.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 12px',
                  border: 'none',
                  borderRadius: 10,
                  background: on ? 'var(--fill-tertiary, #e6f0ff)' : 'var(--fill-quaternary, #f3f3f3)',
                  marginBottom: 6,
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 16 }}>{on ? '☑︎' : '☐'}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{a.name}</span>
                  <span style={{ display: 'block', fontSize: 12, opacity: 0.6 }}>
                    {a.status === 'active' ? 'running' : a.status}
                    {hasAccess ? ' · has access' : ''}
                  </span>
                </span>
                {hasAccess ? (
                  <span style={{ fontSize: 11, color: 'var(--tint, #2b6cff)', fontWeight: 600 }}>✓</span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>

      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '10px 12px',
          borderRadius: 10,
          background: 'var(--fill-quaternary, #f3f3f3)',
          marginBottom: 16,
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={everyone}
          onChange={(e) => onEveryone(e.target.checked)}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <span style={{ fontSize: 13 }}>
          <span style={{ fontWeight: 600 }}>Allow every agent in this household</span>
          <span style={{ display: 'block', opacity: 0.65, marginTop: 2 }}>
            Including agents you create later. Leave off to grant only the agents
            you checked above.
          </span>
        </span>
      </label>

      <button className="wizard__primary" onClick={onNext} disabled={loading}>
        {loading ? 'Loading…' : nextLabel}
      </button>

      {granted.size > 0 && (
        <button
          onClick={onReapply}
          disabled={loading}
          style={{
            display: 'block',
            width: '100%',
            marginTop: 10,
            padding: '8px 12px',
            fontSize: 13,
            background: 'none',
            border: 'none',
            color: 'var(--tint, #2b6cff)',
            cursor: 'pointer',
          }}
        >
          Re-apply the plugin's default rules
          <span style={{ display: 'block', fontSize: 11, opacity: 0.6, color: 'inherit' }}>
            Adds any newer defaults (e.g. after a plugin update) to agents that already
            have access — won't change rules you've customized.
          </span>
        </button>
      )}
    </div>
  )
}

function DoneStep({
  connName,
  added,
  removed,
  everyone,
  reapplied,
  onClose,
}: {
  connName: string
  added: number
  removed: number
  everyone: boolean
  reapplied?: boolean
  onClose: () => void
}) {
  if (reapplied) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 40 }}>✓</div>
        <div style={{ fontSize: 17, fontWeight: 600 }}>Defaults re-applied</div>
        <p style={{ fontSize: 14, opacity: 0.75, lineHeight: 1.5, margin: 0 }}>
          {connName}'s default rules are up to date for the agents with access. Any newer
          defaults were added; your customizations were left alone. Running agents restart
          to pick it up (one mid-task gets it on its next run).
        </p>
        <button className="wizard__primary" onClick={onClose}>
          Done
        </button>
      </div>
    )
  }
  const lines: string[] = []
  if (everyone && added > 0) {
    lines.push(`Every agent in this household can now use ${connName}.`)
  } else if (added > 0) {
    lines.push(`${added} agent${added === 1 ? '' : 's'} can now use ${connName}.`)
  }
  if (removed > 0) {
    lines.push(`${removed} agent${removed === 1 ? '' : 's'} lost access.`)
  }
  if (lines.length === 0) lines.push('No changes were needed.')
  return (
    <div style={{ textAlign: 'center', padding: '24px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 40 }}>✓</div>
      <div style={{ fontSize: 17, fontWeight: 600 }}>Access updated</div>
      <p style={{ fontSize: 14, opacity: 0.75, lineHeight: 1.5, margin: 0 }}>
        {lines.join(' ')}{' '}
        {added > 0
          ? 'Any agents that are running will restart to pick it up (one mid-task is left alone — it gets it on its next run).'
          : ''}
      </p>
      <button className="wizard__primary" onClick={onClose}>
        Done
      </button>
    </div>
  )
}

function titleFor(step: Step, connName: string): string {
  switch (step) {
    case 'agents':
      return `Who can use ${connName}?`
    case 'rules':
      return 'Approve rules'
    case 'submitting':
      return 'Applying…'
    case 'done':
      return 'Done'
  }
}
